<template>
    <page :tabs="tabs" :busy="busy">
        <div v-if="metadata && metadata.airbnb_settings.import_enabled">
            <b-alert class="m-2" show variant="info">
                This is a one-way property import from Airbnb to Lodgix. Its function is to save time, it is not
                perfect. Rates and rules will require some tweaking as Airbnb's fields do not match up 100% with Lodgix
                fields. Additionally, reservation data will NOT import. Existing reservations from Airbnb are only
                created on Lodgix when integrating to Airbnb.
            </b-alert>
            <grid :isAutoHeight="true" :rowData="rowData" :columnDefs="columnDefs"> </grid>
        </div>

        <b-button :href="connectHref" class="ml-2 mt-2" variant="primary" v-else
            ><font-awesome-icon :icon="['fab', 'airbnb']" /> Connect with Airbnb
        </b-button>
        <modal :id="airbnbErrorModalId" title="Error" ok-only>
            An error has occurred while connecting to Airbnb. Please confirm that you aren&apos;t already connected to
            another Lodgix account in your
            <a target="_blank" href="https://www.airbnb.com/account-settings/privacy-and-sharing"
                >Airbnb Connected Apps</a
            >.
        </modal>
    </page>
</template>

<script>
import api from '../../api';
import { AIRBNB_IMPORT_ENDPOINT } from '../../endpoints';
import ActionCellRenderer from './action-cell-renderer.vue';
import Grid from '../generic/grid/grid.vue';
import page from '../generic/page/page.vue';
import Modal from '../generic/modal/modal.vue';
export default {
    components: { page, Grid, Modal, actionCellRenderer: ActionCellRenderer },

    data() {
        return {
            airbnbErrorModalId: 'airbnb-modal-id',
            busy: false,
            tabs: [
                {
                    title: 'Import Airbnb Properties',
                    active: true,
                },
            ],
            metadata: null,
            rowData: [],
        };
    },
    computed: {
        columnDefs() {
            return [
                {
                    field: 'id',
                    headerName: 'Airbnb ID',
                },
                {
                    field: 'name',
                    headerName: 'Name',
                    comparator: (valueA, valueB, nodeA, nodeB) => {
                        return nodeA.data.name.localeCompare(nodeB.data.name);
                    },
                },
                {
                    field: 'address',
                    headerName: 'Address',
                },
                {
                    field: 'lodgix_name',
                    headerName: 'Lodgix Property',
                },
                {
                    field: '',
                    cellRenderer: 'actionCellRenderer',
                    maxWidth: 100,
                    minWidth: 100,
                    cellRendererParams: {
                        importProperty: async (data) => {
                            try {
                                this.busy = true;
                                await api.post(`${AIRBNB_IMPORT_ENDPOINT(data.id)}import-property/`, {
                                    lodgix_id: data.lodgix_id,
                                });
                                this.fetchProperties();
                                this.successToast({ text: 'Property sucessfully imported.' });
                            } catch (e) {
                                this.errorToast({ text: 'Could not import property' });
                            } finally {
                                this.busy = false;
                            }
                        },
                    },
                },
            ];
        },
        connectHref() {
            const url = '/airbnb/import/authorize';
            return url;
        },
    },
    mounted() {
        const redirectError = this.$route.params.error;
        if (redirectError != null) {
            if (redirectError == 'duplicate') {
                this.$bvModal.show(this.airbnbErrorModalId);
            } else {
                this.errorToast({ text: 'An error has occurred while connecting to Airbnb.' });
            }
        }
        this.fetchData();
    },
    methods: {
        async fetchMetaData() {
            try {
                this.busy = true;
                const { data } = await api.get(`${AIRBNB_IMPORT_ENDPOINT()}metadata/`);
                this.metadata = data;
            } catch (e) {
                this.errorToast({ text: 'Could not fetch metadata' });
            } finally {
                this.busy = false;
            }
        },
        async fetchProperties() {
            try {
                this.busy = true;
                const { data } = await api.get(`${AIRBNB_IMPORT_ENDPOINT()}properties/`);
                this.rowData = data;
            } catch (e) {
                this.errorToast({ text: 'Could not fetch properties' });
            } finally {
                this.busy = false;
            }
        },
        async fetchData() {
            await this.fetchMetaData();
            if (this.metadata && this.metadata.airbnb_settings.import_enabled) {
                await this.fetchProperties();
            }
        },
    },
};
</script>

<style>
</style>